import type { PaymentFileRowResult } from '@steerfi/shared';
import { ROW_PROCESSING_STATUS } from '@steerfi/shared';
import type { PaymentWithRelations } from '@/api/payments';

export const PAYMENT_FILE_EMPTY_CELL = '—';

export const PAYMENT_FILE_UNKNOWN_LABEL = 'Unknown';

const ROW_KEY_MISSING_PAYMENT_SEGMENT = 'fail';
const PAYMENT_ID_FALLBACK_LENGTH = 8;

function paymentIdFallbackLabel(payment: PaymentWithRelations): string {
  return payment.id ? payment.id.slice(0, PAYMENT_ID_FALLBACK_LENGTH) : '';
}

function csvReferenceFromRowMeta(meta: PaymentFileRowResult['metadata'] | undefined): string {
  const s = meta?.supplier_reference;
  return typeof s === 'string' ? s.trim() : '';
}

/** SteerFi system payment reference (`external_id`, or truncated UUID fallback). */
export function getPaymentSystemIdDisplay(payment: PaymentWithRelations): string {
  const extId = payment.external_id?.trim() || '';
  const extRef = payment.external_reference?.trim() || '';

  // Legacy rows may have the case ID copied into both columns; keep ID distinct from Case ID.
  if (extId && extRef && extId === extRef) {
    return paymentIdFallbackLabel(payment) || extId;
  }

  if (extId) return extId;
  return paymentIdFallbackLabel(payment);
}

/** Clipboard value for the ID column — always matches {@link getPaymentSystemIdDisplay}. */
export function getPaymentSystemIdCopyValue(payment: PaymentWithRelations): string {
  return getPaymentSystemIdDisplay(payment);
}

/** Tenant case / external reference (`external_reference` only). */
export function getPaymentExternalRefDisplay(payment: PaymentWithRelations): string {
  return payment.external_reference?.trim() || '';
}

export type PaymentFileRowIdCellVm =
  | { variant: 'withPayment' }
  | { variant: 'csvCopy'; csvRef: string }
  | { variant: 'csvOnly'; csvRef: string }
  | { variant: 'loadingPayment' }
  | { variant: 'empty' };

export type PaymentFileRowStatusCellVm =
  | { variant: 'failed' }
  | { variant: 'loadingPayment' }
  | { variant: 'withPayment'; payment: PaymentWithRelations }
  | { variant: 'empty' };

export type PaymentFileRowViewModel = {
  rowKey: string;
  rowNumber: number;
  result: PaymentFileRowResult;
  payment: PaymentWithRelations | undefined;
  paymentId: string | undefined;
  isFailed: boolean;
  payeeLabel: string;
  amountLabel: string | number | null | undefined;
  /** System payment reference (`external_id` or UUID prefix; CSV ref when no payment yet) */
  displayRowId: string;
  /** Value copied when the ID cell is clicked — must match the displayed ID for the row variant */
  displayRowCopyValue: string;
  /** Tenant case / external reference (`external_reference`, or CSV fallback when no payment) */
  displayExternalRef: string;
  idCell: PaymentFileRowIdCellVm;
  statusCell: PaymentFileRowStatusCellVm;
  isRowInteractive: boolean;
};

function deriveIdCellVm(args: {
  isFailed: boolean;
  payment: PaymentWithRelations | undefined;
  csvRef: string;
  paymentId: string | undefined;
  isLoadingPayments: boolean;
}): PaymentFileRowIdCellVm {
  const { isFailed, payment, csvRef, paymentId, isLoadingPayments } = args;

  // Failed rows have a payment stub, but the ID column should prefer the CSV reference for copy.
  if (isFailed && csvRef) return { variant: 'csvCopy', csvRef };
  if (payment) return { variant: 'withPayment' };
  if (paymentId && isLoadingPayments) return { variant: 'loadingPayment' };
  if (csvRef) return { variant: 'csvOnly', csvRef };
  return { variant: 'empty' };
}

function deriveStatusCellVm(args: {
  isFailed: boolean;
  payment: PaymentWithRelations | undefined;
  paymentId: string | undefined;
  isLoadingPayments: boolean;
}): PaymentFileRowStatusCellVm {
  const { isFailed, payment, paymentId, isLoadingPayments } = args;
  if (isFailed) return { variant: 'failed' };
  if (paymentId && !payment && isLoadingPayments) return { variant: 'loadingPayment' };
  if (payment) return { variant: 'withPayment', payment };
  return { variant: 'empty' };
}

export function derivePaymentFileRowViewModel(
  result: PaymentFileRowResult,
  payments: PaymentWithRelations[],
  isLoadingPayments: boolean
): PaymentFileRowViewModel {
  const paymentId = result.record_ids?.payment_id;
  const payment = paymentId ? payments.find((p) => p.id === paymentId) : undefined;
  const meta = result.metadata;
  const csvRef = csvReferenceFromRowMeta(meta);
  const isFailed = result.status === ROW_PROCESSING_STATUS.FAILED;

  const payeeLabel = isFailed
    ? meta?.payee_display || meta?.supplier_reference || PAYMENT_FILE_EMPTY_CELL
    : payment?.payee_name ||
      payment?.payee_party?.party_name ||
      meta?.payee_display ||
      PAYMENT_FILE_EMPTY_CELL;

  const amountLabel = isFailed ? meta?.amount : payment?.amount_total;

  const rowKey = `row-${result.row_number}-${paymentId ?? ROW_KEY_MISSING_PAYMENT_SEGMENT}`;

  const displayRowId =
    payment != null
      ? getPaymentSystemIdDisplay(payment)
      : paymentId && isLoadingPayments
        ? ''
        : csvRef;

  const displayRowCopyValue =
    isFailed && csvRef
      ? csvRef
      : payment
        ? getPaymentSystemIdCopyValue(payment)
        : paymentId && isLoadingPayments
          ? ''
          : csvRef;

  const displayExternalRef = payment ? getPaymentExternalRefDisplay(payment) : csvRef;

  const idCell = deriveIdCellVm({ isFailed, payment, csvRef, paymentId, isLoadingPayments });
  const statusCell = deriveStatusCellVm({ isFailed, payment, paymentId, isLoadingPayments });
  const isRowInteractive = Boolean(!isFailed && payment);

  return {
    rowKey,
    rowNumber: result.row_number,
    result,
    payment,
    paymentId,
    isFailed,
    payeeLabel,
    amountLabel,
    displayRowId,
    displayRowCopyValue,
    displayExternalRef,
    idCell,
    statusCell,
    isRowInteractive,
  };
}
